import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {BenchlingExpressionFunction} from 'benchling-expressions/BenchlingExpressionTypes';
import deepCopy from 'lib/deepCopy';
import {
  createNewDeliveryDateExpr,
  createNewRecipientExpr,
  createNewSetTimeOnDatetimeExpr,
  currentEvaluatorVersion,
  getDaysFromExprV1,
  getRecipientFromExprV1,
  getSubtractFromDatetimeFromExprV1,
  NotificationSchedule,
  RecipientType,
} from 'notifications/notification-schedule/NotificationScheduleExpressionUtils';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import NumericInput from 'ui-library/inputs/NumericInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {NumericInputChangeEvent} from 'util/Types';

interface NotificationScheduleEditorAttributes {
  recipient: RecipientType;
  days: number;
  deliveryDirection: DeliveryDirection;
}

/** @package Exported for tests. */
export const RECIPIENT_TYPE_TO_DISPLAY_TEXT: Record<RecipientType, string> = {
  [RecipientType.ASSIGNEE]: 'Assignee',
  [RecipientType.WATCHERS]: 'Watchers',
};

export enum DeliveryDirection {
  BEFORE = 'Before',
  AFTER = 'After',
}

/** @package Exported for tests. */
export const DELIVERY_DIRECTION_TO_DISPLAY_TEXT: Record<DeliveryDirection, string> = {
  [DeliveryDirection.BEFORE]: 'Before Due',
  [DeliveryDirection.AFTER]: 'After Due',
};

const MIN_DAYS_ALLOWED = 0;
const MAX_DAYS_ALLOWED = 365;

export interface DeliveryTimeParams {
  second: number;
  minute: number;
  hour: number;
}

export function createNewDeliveryDateExprFromUI(
  days: number,
  deliveryDirection: DeliveryDirection,
  deliveryTime: DeliveryTimeParams
): BenchlingExpressionFunction {
  const subtractFromDatetime = deliveryDirection === DeliveryDirection.BEFORE;
  const {second, minute, hour} = deliveryTime;
  const datetimeExpr = createNewSetTimeOnDatetimeExpr(second, minute, hour);
  return createNewDeliveryDateExpr(days, subtractFromDatetime, datetimeExpr);
}

function getNotificationScheduleAttributes(
  notificationSchedule: NotificationSchedule
): NotificationScheduleEditorAttributes {
  const recipient = getRecipientFromExprV1(notificationSchedule.recipientExpr);
  const days = getDaysFromExprV1(notificationSchedule.deliveryDateExpr);
  const subtractFromDatetime = getSubtractFromDatetimeFromExprV1(notificationSchedule.deliveryDateExpr);
  const deliveryDirection = subtractFromDatetime ? DeliveryDirection.BEFORE : DeliveryDirection.AFTER;
  return {
    recipient,
    days,
    deliveryDirection,
  };
}

export interface NotificationScheduleEditorRowProps {
  notificationSchedule: NotificationSchedule;
  defaultDeliveryTime: DeliveryTimeParams;
  onUpdate: (notificationSchedule: NotificationSchedule) => void;
  readOnly: boolean;
}

export default function NotificationScheduleEditorRow(
  props: NotificationScheduleEditorRowProps
): JSX.Element {
  const {recipient, days, deliveryDirection} = getNotificationScheduleAttributes(props.notificationSchedule);

  const RECIPIENT_OPTIONS = [RecipientType.ASSIGNEE];

  const onUpdateRecipient = (newRecipient: RecipientType): void => {
    const newRecipientExpr = createNewRecipientExpr(newRecipient);
    const newSchedule = {
      recipientExpr: newRecipientExpr,
      deliveryDateExpr: deepCopy(props.notificationSchedule.deliveryDateExpr),
      evaluatorVersion: currentEvaluatorVersion(),
    };
    props.onUpdate(newSchedule);
  };

  const onUpdateDays = (newDays: NumericInputChangeEvent): void => {
    if (newDays.target.numericValue == null) {
      return;
    }
    const newDeliveryDateExpr = createNewDeliveryDateExprFromUI(
      newDays.target.numericValue,
      deliveryDirection,
      props.defaultDeliveryTime
    );
    const newSchedule = {
      recipientExpr: deepCopy(props.notificationSchedule.recipientExpr),
      deliveryDateExpr: newDeliveryDateExpr,
      evaluatorVersion: currentEvaluatorVersion(),
    };
    props.onUpdate(newSchedule);
  };

  const onUpdateDeliveryDirection = (newDeliveryDirection: DeliveryDirection): void => {
    const newDeliveryDateExpr = createNewDeliveryDateExprFromUI(
      days,
      newDeliveryDirection,
      props.defaultDeliveryTime
    );
    const newSchedule = {
      recipientExpr: deepCopy(props.notificationSchedule.recipientExpr),
      deliveryDateExpr: newDeliveryDateExpr,
      evaluatorVersion: currentEvaluatorVersion(),
    };
    props.onUpdate(newSchedule);
  };

  return (
    <div className={css(styles.formGrow)}>
      <Row>
        <Col sm={4} className={css(styles.column)}>
          <Label className={css(styles.label)}>Recipient</Label>
          <SelectDropdown
            alwaysDropDown={true}
            displayName={RECIPIENT_TYPE_TO_DISPLAY_TEXT[recipient]}
            disabled={props.readOnly}
            dataTestAttrs={{component: 'NotificationScheduleEditorRow', element: 'recipientDropdown'}}
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                {RECIPIENT_OPTIONS.map((recipientType) => (
                  <Option key={recipientType} onSelect={onUpdateRecipient} value={recipientType}>
                    {RECIPIENT_TYPE_TO_DISPLAY_TEXT[recipientType]}
                  </Option>
                ))}
              </Menu>
            }
          />
        </Col>
        <Col sm={4} className={css(styles.column)}>
          <Label className={css(styles.label)}>Notify (in days)</Label>
          <NumericInput
            value={days}
            onChange={onUpdateDays}
            isDisabled={props.readOnly}
            min={MIN_DAYS_ALLOWED}
            max={MAX_DAYS_ALLOWED}
            validateInteger={true}
          />
        </Col>
        <Col sm={4} className={css(styles.column)}>
          <Label className={css(styles.label)}>Before/After</Label>
          <SelectDropdown
            alwaysDropDown={true}
            displayName={DELIVERY_DIRECTION_TO_DISPLAY_TEXT[deliveryDirection]}
            disabled={props.readOnly}
            dataTestAttrs={{component: 'NotificationScheduleEditorRow', element: 'deliveryDirectionDropdown'}}
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                {Object.values(DeliveryDirection).map((deliveryDirection) => (
                  <Option
                    key={deliveryDirection}
                    onSelect={onUpdateDeliveryDirection}
                    value={deliveryDirection}
                  >
                    {DELIVERY_DIRECTION_TO_DISPLAY_TEXT[deliveryDirection]}
                  </Option>
                ))}
              </Menu>
            }
          />
        </Col>
      </Row>
    </div>
  );
}

const styles = StyleSheet.create({
  formGrow: {
    flex: 'auto',
  },
  column: {paddingLeft: 20},
  label: {paddingLeft: 0},
});
